// "No maintenance history on file for this lift." (Session AMF, closing Session ALW's second
// flagged gap.)
//
// THE GAP THIS CLOSES. Session ALW made raising a Full Condition Report a real platform action,
// from chat and (Session AMF) from the office. What it did NOT do -- and said so plainly -- was
// make the platform NOTICE when one was due. The rule that a lift with no documented routine
// maintenance in the previous 12 months needs one before ELE quotes or services it lived
// entirely in the office manager's head, so it held only as long as she remembered it on the
// particular morning a particular enquiry arrived.
//
// IT ASKS. IT NEVER ACTS. Nothing here raises anything: it puts the question on the screen where
// the decision is being taken and offers the control. The office still decides, which matters
// because the platform holds ELE's own records only -- a lift maintained impeccably by somebody
// else, with a folder of certificates ELE has never seen, is indistinguishable here from a lift
// nobody has touched for years. The rule for what counts, and that honest limit, live in one place
// (src/lib/conditionReportTrigger.ts); this file is only its face.
//
// TWO CALLERS, DELIBERATELY, AND THEY ARE THE TWO ENDS OF THE SAME MOMENT: the lift's own record,
// where the office looks at a lift, and the LOLER report detail page's "Create maintenance quote"
// action, which is literally the pre-contract enquiry becoming a quote. A LOLER report arriving
// for a lift ELE does not maintain is the commonest way this exact situation reaches the office,
// and it is the one the published rule names a LOLER report as NOT answering.
import { useState } from "react";
import { CONDITION_REPORT_FEE_GBP, type ConditionReportTrigger } from "../../lib/conditionReportTrigger";
import { RaiseWorkModal, raisedWorkMessage } from "./RaiseWorkModal";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

export function ConditionReportPrompt({
  trigger,
  assetId,
  liftRef,
  onRaised,
}: {
  trigger: ConditionReportTrigger;
  assetId: string;
  // How the office refers to this lift (its ELE number), for the confirmation sentence.
  liftRef?: string | null;
  // Called once a condition report was raised (or an existing one re-opened), with the sentence to
  // show. The host owns its own notice area and its own reload; this component owns neither.
  onRaised: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!trigger.required) return null;
  return (
    <>
      {/* Amber, not red: this is an attention-required advisory in the locked functional palette,
          not a fault. Nothing is broken and nothing is overdue -- ELE simply has no record. */}
      <div data-testid="condition-report-prompt" role="status" className="mb-4 rounded-ele border border-amber-400 bg-ele-surface p-4">
        <h2 className="font-heading text-base text-amber-400">No maintenance history on file for this lift</h2>
        <p className="mt-1 text-sm text-ele-body">
          A Full Condition Report is required before quoting or servicing a lift with no documented evidence of routine maintenance in the previous 6 to
          12 months. It is the published Schedule of Fees rule, at GBP {CONDITION_REPORT_FEE_GBP} ex VAT. Raise one?
        </p>
        {/* Said explicitly because it is the single likeliest reason the office would look at this
            prompt and think it is wrong. A LOLER thorough examination is a statutory examination by
            an independent competent person; it is not a record of routine maintenance, and ELE's own
            published fees say in terms that one does not remove this requirement. */}
        <p className="mt-2 text-xs text-ele-body opacity-80">
          A LOLER thorough examination on file does not answer this: it is a separate statutory examination, not a record of routine maintenance. If ELE
          does hold evidence of servicing from a previous provider, record it as the lift's last serviced date instead and this will clear.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Raise a Condition Report
          </Button>
          {trigger.lastEvidence && <span className="text-xs text-ele-body">Last recorded service: {formatDate(trigger.lastEvidence)}.</span>}
        </div>
      </div>
      {open && (
        <RaiseWorkModal
          preset={{ type: "condition_report", assetId }}
          onClose={() => setOpen(false)}
          onRaised={(result) => {
            setOpen(false);
            onRaised(`${raisedWorkMessage(result)}${liftRef ? ` (${liftRef})` : ""}`);
          }}
        />
      )}
    </>
  );
}
